// src/prototype/components/execute/ExecApprovalTab.tsx
// ============================================================
// Execute hub · Approval tab (E5, Oct 21; interactive workflow v3)
//
// Each approval step is a working card: owner and due date (overdue flagged), a checklist of
// required actions, a notes thread and the decision. Approve unlocks when the checklist is
// complete; defer and decline are always available (decline records what would reverse it).
// A decided step can be reopened with a reason. Every action is stamped with the "Acting as"
// persona and lands in one activity log (shared with milestone notes and status changes).
// The final decision captures the decision snapshot (decision half of decision -> outcome).
// Client-side state for the demo.
//
// Artifact Export is its own section; the print routes are fixed to the featured use case.
// ============================================================
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Scale, FileText, BookOpen, Layers, CheckCircle2, Circle, XCircle, PauseCircle, Download, RotateCcw,
  ChevronDown, ChevronRight, MessageSquare, User, CalendarClock, Activity, Lock, DollarSign,
} from 'lucide-react';
import { SectionCard } from '../SectionCard';
import { CFOPanel, appendReturnTo } from '../CFOPanel';
import type { InitiativeRecord } from '../../data/executionInitiatives';
import {
  PERSONAS, personaById, actorLabel, type Persona, type ResolvedStep, type Signoff, type SignoffDecision,
} from '../../data/execute/approvalWorkflow';
import type { useExecuteHubState, Note, ActivityEvent, DecisionSnapshot } from '../../hooks/useExecuteHubState';

export { resolveSteps } from '../../data/execute/approvalWorkflow';

/** The print artifacts are generated for this use case only, until per-initiative builders are wired. */
export const ARTIFACT_USE_CASE_ID = 'opp-1';

const ARTIFACTS = [
  { label: 'CFO Summary', reader: 'CFO: can the number be defended', contents: 'Investment, net 3-year value, payback and scenario ranges; key financial assumptions; sensitivity on the top drivers; risks with financial exposure.', route: '/use-case/cfo-summary/print', Icon: FileText },
  { label: 'Board Rationale Packet', reader: 'Board or governance committee: why this over the alternatives', contents: 'Decision requested; evaluation verdict and dimension scores; alignment to stated priorities; position against alternatives; risks and mitigations; approval trail.', route: '/use-case/board-rationale/print', Icon: BookOpen },
  { label: 'Executive Brief', reader: 'Executive sponsor: one page', contents: 'What it is, value range, time to value, the approval requested, owner and next steps.', route: '/use-case/executive-summary/print', Icon: FileText },
  { label: 'Assumption Appendix', reader: 'Analyst or auditor: traceability', contents: 'Every financial and operational assumption with value, source and whether it was client-edited; the data inputs used.', route: '/use-case/assumptions/print', Icon: Layers },
] as const;

const DECISION_LABEL: Record<SignoffDecision, string> = { approved: 'Approved', deferred: 'Deferred', declined: 'Declined' };
const input = 'text-xs text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1.5 bg-white w-full focus:outline-none focus:ring-1 focus:ring-[#18365E]/20';

export const fmtWhen = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtDate = (ymd?: string) => (ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

type Hub = ReturnType<typeof useExecuteHubState>;

/** Notes thread used by approval steps and milestones. */
export function NotesThread({ notes, onAdd, placeholder = 'Add a note' }: { notes: Note[]; onAdd: (text: string) => void; placeholder?: string }) {
  const [text, setText] = useState('');
  return (
    <div className="space-y-2">
      {notes.length > 0 && (
        <ul className="space-y-1.5">
          {notes.map((n) => (
            <li key={n.id} className="rounded-md bg-white border border-[#e5e7eb] px-2.5 py-1.5 text-xs">
              <p className="text-[#4b5563] whitespace-pre-wrap">{n.text}</p>
              <p className="text-[10px] text-[#717182] mt-0.5">{n.author} · {fmtWhen(n.at)}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2 items-start">
        <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} aria-label={placeholder} className={`${input} resize-y`} />
        <button type="button" disabled={!text.trim()} onClick={() => { onAdd(text.trim()); setText(''); }}
          className="shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#18365E] text-white disabled:opacity-40">Add note</button>
      </div>
    </div>
  );
}

/** Activity timeline (most recent first). */
export function ActivityLog({ events, limit }: { events: ActivityEvent[]; limit?: number }) {
  const shown = limit ? events.slice(0, limit) : events;
  if (shown.length === 0) return <p className="text-xs text-[#717182]">No activity yet. Checklist ticks, notes, decisions and milestone updates appear here.</p>;
  return (
    <ol className="space-y-2.5">
      {shown.map((e) => (
        <li key={e.id} className="flex gap-2 text-xs">
          <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${e.kind === 'decision' ? 'bg-[#FF6E4C]' : e.kind === 'reopen' ? 'bg-amber-500' : e.area === 'milestone' ? 'bg-sky-500' : 'bg-[#18365E]'}`} />
          <div className="min-w-0">
            <p className="text-[#18365E]"><b>{e.target}</b> <span className="text-[#4b5563]">{e.detail}</span></p>
            <p className="text-[10px] text-[#717182]">{e.actor} · {fmtWhen(e.at)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function DecisionForm({ step, persona, onSubmit }: { step: ResolvedStep; persona: Persona; onSubmit: (s: Signoff) => void }) {
  const [decision, setDecision] = useState<SignoffDecision>('approved');
  const [rationale, setRationale] = useState('');
  const [reversal, setReversal] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const approveLocked = decision === 'approved' && !step.checklistComplete;
  const missing = !rationale.trim() || (decision === 'declined' && !reversal.trim()) || approveLocked;
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (!missing) onSubmit({ approver: persona.name, role: persona.role, date, decision, rationale: rationale.trim(), ...(decision === 'declined' ? { reversal: reversal.trim() } : {}) }); }}
      className="rounded-lg border border-[#e5e7eb] bg-white p-3 space-y-2"
      aria-label={`Record decision: ${step.label}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-[#e5e7eb] bg-white p-0.5" role="radiogroup" aria-label="Decision">
          {(['approved', 'deferred', 'declined'] as SignoffDecision[]).map((d) => (
            <button key={d} type="button" role="radio" aria-checked={decision === d} onClick={() => setDecision(d)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${decision === d ? 'bg-[#18365E] text-white' : 'text-[#717182] hover:text-[#18365E]'}`}>
              {DECISION_LABEL[d]}
            </button>
          ))}
        </div>
        <input className={`${input} w-auto`} type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Decision date" />
        <span className="text-[10px] text-[#717182]">as {actorLabel(persona)}</span>
      </div>
      {approveLocked && (
        <p className="text-[11px] text-amber-800 flex items-center gap-1"><Lock className="w-3 h-3" /> Approve unlocks when every checklist item is complete. Defer or decline is available now.</p>
      )}
      <textarea className={input} rows={2} placeholder="Rationale (required)" value={rationale} onChange={(e) => setRationale(e.target.value)} aria-label="Rationale" />
      {decision === 'declined' && (
        <textarea className={input} rows={2} placeholder="What changes would reverse this decision? (required)" value={reversal} onChange={(e) => setReversal(e.target.value)} aria-label="Changes that would reverse the decision" />
      )}
      <button type="submit" disabled={missing} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FF6E4C] text-white disabled:opacity-40">
        Record decision
      </button>
    </form>
  );
}

function ReopenForm({ onSubmit }: { onSubmit: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} className="text-[11px] font-semibold text-[#717182] hover:text-[#18365E] flex items-center gap-1">
      <RotateCcw className="w-3 h-3" /> Reopen this step
    </button>
  );
  return (
    <div className="flex gap-2 items-start">
      <input className={input} placeholder="Reason for reopening (required)" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason for reopening" />
      <button type="button" disabled={!reason.trim()} onClick={() => { onSubmit(reason.trim()); setReason(''); setOpen(false); }}
        className="shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-[#18365E]/30 text-[#18365E] disabled:opacity-40">Reopen</button>
      <button type="button" onClick={() => setOpen(false)} className="shrink-0 px-2 py-1.5 text-xs text-[#717182]">Cancel</button>
    </div>
  );
}

function StepCard({ step, expanded, onToggle, notes, persona, hub, onDecide }: {
  step: ResolvedStep; expanded: boolean; onToggle: () => void; notes: Note[]; persona: Persona; hub: Hub;
  onDecide: (s: Signoff) => void;
}) {
  const actor = actorLabel(persona);
  const owner = personaById(step.ownerId);
  const doneCount = step.items.filter((x) => x.done).length;
  const Icon = step.status === 'done' ? CheckCircle2 : step.status === 'blocked' ? (step.decision?.decision === 'deferred' ? PauseCircle : XCircle) : Circle;
  const tone = step.status === 'done' ? 'text-emerald-600' : step.status === 'blocked' ? 'text-red-600' : step.status === 'current' ? 'text-[#FF6E4C]' : 'text-[#c4cad4]';
  const statusText = step.decision ? `${DECISION_LABEL[step.decision.decision]} by ${step.decision.approver}` : step.status === 'done' ? 'Complete (initiative record)' : step.status === 'current' ? 'In progress' : 'Not started';

  return (
    <li className={`rounded-lg border ${step.status === 'current' ? 'border-[#FF6E4C]/50' : 'border-[#e5e7eb]'} bg-white`}>
      <button type="button" onClick={onToggle} aria-expanded={expanded} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left">
        <Icon className={`w-4 h-4 shrink-0 ${tone}`} aria-hidden="true" />
        <span className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-[#18365E] flex items-center gap-1.5 flex-wrap">
            {step.label}
            {step.cfo && <span className="text-[9px] font-bold uppercase text-[#FF6E4C] border border-[#FF6E4C]/40 rounded px-1">CFO gate</span>}
            {step.overdue && <span className="text-[9px] font-bold uppercase text-white bg-red-600 rounded px-1">Overdue</span>}
          </span>
          <span className="text-[10px] text-[#717182]">{statusText} · {owner.name}{step.due ? ` · due ${fmtDate(step.due)}` : ''} · {doneCount}/{step.items.length} done{notes.length ? ` · ${notes.length} note${notes.length === 1 ? '' : 's'}` : ''}</span>
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-[#717182]" /> : <ChevronRight className="w-4 h-4 text-[#717182]" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-[#f1f5f9] space-y-3 bg-[#FAFBFC] rounded-b-lg">
          {step.note && <p className="text-[11px] text-[#717182] italic">{step.note}</p>}

          <div className="grid sm:grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide flex items-center gap-1"><User className="w-3 h-3" />Owner</span>
              <select className={input} value={step.ownerId} onChange={(e) => hub.setStepOwner(step.index, step.label, e.target.value, personaById(e.target.value).name, actor)} aria-label={`${step.label} owner`}>
                {PERSONAS.map((p) => <option key={p.id} value={p.id}>{actorLabel(p)}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide flex items-center gap-1"><CalendarClock className="w-3 h-3" />Due date</span>
              <input type="date" className={input} value={step.due ?? ''} onChange={(e) => hub.setStepDue(step.index, step.label, e.target.value, actor)} aria-label={`${step.label} due date`} />
            </label>
          </div>

          <div>
            <p className="text-[10px] font-bold text-[#717182] uppercase tracking-wide mb-1">Required actions</p>
            <ul className="space-y-1">
              {step.items.map((it) => (
                <li key={it.label}>
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input type="checkbox" className="mt-0.5 accent-[#18365E]" checked={it.done}
                      onChange={(e) => hub.toggleCheck(step.index, step.label, it.label, e.target.checked, actor)} aria-label={it.label} />
                    <span className={it.done ? 'text-[#717182] line-through' : 'text-[#18365E]'}>{it.label}</span>
                    {it.done && it.by && <span className="ml-auto text-[10px] text-[#717182] whitespace-nowrap">{it.by.split(' (')[0]}{it.at ? ` · ${fmtWhen(it.at)}` : ''}</span>}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[10px] font-bold text-[#717182] uppercase tracking-wide mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" />Notes</p>
            <NotesThread notes={notes} onAdd={(text) => hub.addNote({ kind: 'approval', step: step.index, label: step.label }, text, actor)} />
          </div>

          {step.decision && (
            <div className="text-[11px] text-[#4b5563] rounded-md bg-white border border-[#e5e7eb] px-2.5 py-2">
              <p><b className="text-[#18365E]">{DECISION_LABEL[step.decision.decision]}</b> by {step.decision.approver} ({step.decision.role}) on {fmtDate(step.decision.date)}</p>
              <p className="italic mt-0.5">{step.decision.rationale}</p>
              {step.decision.reversal && <p className="mt-0.5"><b>Would reverse if:</b> {step.decision.reversal}</p>}
            </div>
          )}

          {step.status === 'current' && <DecisionForm step={step} persona={persona} onSubmit={onDecide} />}
          {step.status === 'pending' && <p className="text-[11px] text-[#717182]">The decision opens when the earlier steps are complete. Actions and notes can be prepared now.</p>}
          {(step.status === 'done' || step.status === 'blocked') && (
            <ReopenForm onSubmit={(reason) => hub.reopen(step.index, step.label, reason, step.decision ? DECISION_LABEL[step.decision.decision].toLowerCase() : 'complete in the initiative record', actor)} />
          )}
        </div>
      )}
    </li>
  );
}

export interface ExecApprovalTabProps {
  useCaseId: string;
  record: InitiativeRecord;
  steps: ResolvedStep[];
  notes: Note[];
  activity: ActivityEvent[];
  snapshot?: DecisionSnapshot;
  persona: Persona;
  hub: Hub;
  onDecide: (step: ResolvedStep, s: Signoff) => void;
  onOpenRoiModel: () => void;
}

export const ExecApprovalTab: React.FC<ExecApprovalTabProps> = ({ useCaseId, record, steps, notes, activity, snapshot, persona, hub, onDecide, onOpenRoiModel }) => {
  const location = useLocation();
  const artifactsReady = useCaseId === ARTIFACT_USE_CASE_ID;
  const currentIdx = steps.find((s) => s.status === 'current' || s.status === 'blocked')?.index;
  const [open, setOpen] = useState<Set<number>>(() => new Set(currentIdx !== undefined ? [currentIdx] : []));
  const toggle = (i: number) => setOpen((prev) => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const [filter, setFilter] = useState<'all' | 'approval' | 'milestone'>('all');
  const shownActivity = useMemo(() => activity.filter((a) => filter === 'all' || a.area === filter), [activity, filter]);
  const stepsDone = steps.filter((s) => s.status === 'done').length;
  const hasEdits = activity.some((a) => a.area === 'approval');

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
        <SectionCard icon={<Scale className="w-3.5 h-3.5" />} title={`Approval and sign-off · ${stepsDone} of ${steps.length} steps complete`} accent>
          <ol className="space-y-2">
            {steps.map((s) => (
              <StepCard key={s.label} step={s} expanded={open.has(s.index)} onToggle={() => toggle(s.index)}
                notes={notes.filter((n) => n.target.kind === 'approval' && n.target.step === s.index)}
                persona={persona} hub={hub} onDecide={(so) => onDecide(s, so)} />
            ))}
          </ol>
          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-[10px] text-[#717182]">Saved in this browser for the demo. Actions are stamped with the person selected in "Acting as".</p>
            {hasEdits && (
              <button onClick={hub.clearApproval} className="text-[11px] font-semibold text-[#717182] hover:text-[#18365E] flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Reset approval workflow
              </button>
            )}
          </div>
        </SectionCard>

        <SectionCard icon={<Activity className="w-3.5 h-3.5" />} title="Activity">
          <div className="inline-flex rounded-lg border border-[#e5e7eb] bg-white p-0.5 mb-3" role="radiogroup" aria-label="Activity filter">
            {([['all', 'All'], ['approval', 'Approval'], ['milestone', 'Milestones']] as const).map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={filter === k} onClick={() => setFilter(k)}
                className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium ${filter === k ? 'bg-[#18365E] text-white' : 'text-[#717182] hover:text-[#18365E]'}`}>{l}</button>
            ))}
          </div>
          <div className="max-h-[560px] overflow-y-auto pr-1">
            <ActivityLog events={shownActivity} />
          </div>
        </SectionCard>
      </div>

      <details className="rounded-xl border border-[#e5e7eb] bg-white">
        <summary className="px-4 py-3 text-xs font-semibold text-[#18365E] cursor-pointer flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" />CFO financial summary</summary>
        <div className="px-4 pb-4">
          <CFOPanel cfo={record.cfo} onOpenRoiModel={onOpenRoiModel} showArtifacts={false} />
        </div>
      </details>

      <SectionCard icon={<CheckCircle2 className="w-3.5 h-3.5" />} title="Decision record">
        {snapshot ? (
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2.5 text-xs">
            {[
              ['Decision', `${DECISION_LABEL[snapshot.decision]} on ${fmtDate(snapshot.date)}`],
              ['Approvers', snapshot.approvers.join(', ') || 'None recorded'],
              ['Composite at decision', `${snapshot.compositeScore} / 100`],
              ['Verdict at decision', snapshot.verdict],
              ['Annual value range', snapshot.annualValueRange],
              ['Net 3-year value (base)', snapshot.netValue3Yr],
              ['Payback (base)', snapshot.payback],
              ['Scores as of', `${snapshot.scoresAsOf} · ${snapshot.scoringRun}`],
            ].map(([k, v]) => (
              <div key={k}><dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">{k}</dt><dd className="font-bold text-[#18365E] mt-0.5">{v}</dd></div>
            ))}
            {snapshot.keyAssumptions.length > 0 && (
              <div className="col-span-2 md:col-span-4">
                <dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">Key assumptions at decision</dt>
                <dd className="text-[#4b5563] mt-0.5">{snapshot.keyAssumptions.join(' · ')}</dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="text-xs text-[#717182]">
            Captured when the final step is approved, or when any step is deferred or declined: the decision and date, approvers,
            the composite score and verdict, the scoring run, the value range and the key assumptions behind it.
          </p>
        )}
      </SectionCard>

      <SectionCard icon={<Download className="w-3.5 h-3.5" />} title="Artifact export">
        {!artifactsReady && (
          <p className="text-[11px] text-[#717182] mb-3">Artifacts are generated for the featured use case in this version. Per-use-case generation is next.</p>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          {ARTIFACTS.map(({ label, reader, contents, route, Icon }) => (
            <div key={label} className="rounded-lg border border-[#e5e7eb] bg-white p-3.5 flex flex-col">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-[#18365E]" />
                <p className="text-sm font-bold text-[#18365E]">{label}</p>
                <span className="ml-auto text-[10px] font-semibold text-[#717182] border border-[#e5e7eb] rounded px-1.5 py-0.5">PDF</span>
              </div>
              <p className="text-[11px] font-semibold text-[#4b5563] mt-1.5">{reader}</p>
              <p className="text-[11px] text-[#717182] mt-1 flex-1">{contents}</p>
              <button
                disabled={!artifactsReady}
                onClick={() => window.open(appendReturnTo(route, location.pathname), '_blank')}
                className="mt-3 self-start text-xs font-semibold text-[#18365E] border border-[#18365E]/30 rounded-lg px-3 py-1.5 hover:bg-[#18365E] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#18365E]"
              >
                Open and print
              </button>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#717182] mt-3">Every artifact carries the client name, the scores-as-of date and ranges rather than point estimates.</p>
      </SectionCard>
    </div>
  );
};

export default ExecApprovalTab;
